import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  TextInput,
  NumberInput,
  MultiSelect,
  Switch,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Divider,
  Text,
  Box,
} from "@mantine/core";
import type { TollLocation, TollVehicleType } from "../../types/domain";

export interface TollLocationFormValues {
  name: { en: string; sw: string };
  latitude: number;
  longitude: number;
  radiusMeters: number;
  feeTZS: number;
  appliesToVehicleTypes: TollVehicleType[];
  isActive: boolean;
}

const VEHICLE_OPTIONS: { value: TollVehicleType; label: string }[] = [
  { value: "car", label: "Car (taxi)" },
  { value: "motorcycle", label: "Motorcycle (boda)" },
  { value: "tricycle", label: "Tricycle (bajaj)" },
];

// Default centre = central Dar es Salaam, so the preview map isn't blank.
const EMPTY: TollLocationFormValues = {
  name: { en: "", sw: "" },
  latitude: -6.7924,
  longitude: 39.2083,
  radiusMeters: 150,
  feeTZS: 2000,
  appliesToVehicleTypes: ["car", "tricycle"],
  isActive: true,
};

function fromTollLocation(t: TollLocation): TollLocationFormValues {
  const [lng, lat] = t.center?.coordinates ?? [
    EMPTY.longitude,
    EMPTY.latitude,
  ];
  return {
    name: { en: t.name?.en ?? "", sw: t.name?.sw ?? "" },
    latitude: lat,
    longitude: lng,
    radiusMeters: t.radiusMeters ?? EMPTY.radiusMeters,
    feeTZS: t.feeTZS ?? EMPTY.feeTZS,
    appliesToVehicleTypes:
      t.appliesToVehicleTypes ?? EMPTY.appliesToVehicleTypes,
    isActive: t.isActive ?? true,
  };
}

/**
 * Live preview of the toll pin on an OpenStreetMap embed. This is a read-only
 * preview (no API key, no dependency): the admin sets coordinates via the
 * numeric inputs and the marker/box updates to confirm the location and rough
 * radius. A click-to-pick Google map can be layered on later once a Maps key is
 * wired into the admin app.
 */
function OsmPreview({
  lat,
  lon,
  radiusMeters,
}: {
  lat: number;
  lon: number;
  radiusMeters: number;
}) {
  const src = useMemo(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    // Convert the radius (plus padding) to a lon/lat delta for the bbox.
    const metresToLatDeg = 1 / 111_320;
    const pad = Math.max(radiusMeters * 3, 200);
    const dLat = pad * metresToLatDeg;
    const dLon =
      pad * metresToLatDeg / Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
    const bbox = [lon - dLon, lat - dLat, lon + dLon, lat + dLat].join(",");
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;
  }, [lat, lon, radiusMeters]);

  if (!src) return null;
  return (
    <Box>
      <Text size="xs" c="dimmed" mb={4}>
        Preview (pin marks the toll centre)
      </Text>
      <iframe
        title="Toll location preview"
        src={src}
        style={{
          width: "100%",
          height: 240,
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: 8,
        }}
      />
      <Text size="xs" c="dimmed" mt={4}>
        Charged when a trip's route passes within {radiusMeters} m of this point.
      </Text>
    </Box>
  );
}

export function TollLocationFormModal({
  opened,
  onClose,
  editing,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  editing: TollLocation | null;
  loading: boolean;
  onSubmit: (values: TollLocationFormValues) => void;
}) {
  const [v, setV] = useState<TollLocationFormValues>(EMPTY);

  useEffect(() => {
    if (opened) setV(editing ? fromTollLocation(editing) : EMPTY);
  }, [opened, editing]);

  const valid =
    v.name.en.trim() &&
    v.name.sw.trim() &&
    v.appliesToVehicleTypes.length > 0 &&
    Number.isFinite(v.latitude) &&
    Number.isFinite(v.longitude) &&
    v.radiusMeters > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? "Edit toll location" : "New toll location"}
      size="lg"
      centered
    >
      <Stack>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          Name (bilingual)
        </Text>
        <SimpleGrid cols={2}>
          <TextInput
            label="Name (English)"
            placeholder="Nyerere Bridge"
            value={v.name.en}
            onChange={(e) =>
              setV({ ...v, name: { ...v.name, en: e.currentTarget.value } })
            }
            required
          />
          <TextInput
            label="Name (Swahili)"
            placeholder="Daraja la Nyerere"
            value={v.name.sw}
            onChange={(e) =>
              setV({ ...v, name: { ...v.name, sw: e.currentTarget.value } })
            }
            required
          />
        </SimpleGrid>

        <Divider label="Location & radius" labelPosition="left" />
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <NumberInput
            label="Latitude"
            value={v.latitude}
            onChange={(val) => setV({ ...v, latitude: Number(val) })}
            decimalScale={6}
            step={0.0001}
            min={-90}
            max={90}
          />
          <NumberInput
            label="Longitude"
            value={v.longitude}
            onChange={(val) => setV({ ...v, longitude: Number(val) })}
            decimalScale={6}
            step={0.0001}
            min={-180}
            max={180}
          />
          <NumberInput
            label="Radius (metres)"
            value={v.radiusMeters}
            onChange={(val) => setV({ ...v, radiusMeters: Number(val) || 0 })}
            min={1}
            max={20000}
          />
        </SimpleGrid>

        <OsmPreview
          lat={v.latitude}
          lon={v.longitude}
          radiusMeters={v.radiusMeters}
        />

        <Divider label="Fee & applicability" labelPosition="left" />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <NumberInput
            label="Fee (TZS)"
            value={v.feeTZS}
            onChange={(val) => setV({ ...v, feeTZS: Number(val) || 0 })}
            min={0}
            thousandSeparator=","
          />
          <MultiSelect
            label="Applies to vehicle types"
            data={VEHICLE_OPTIONS}
            value={v.appliesToVehicleTypes}
            onChange={(val) =>
              setV({ ...v, appliesToVehicleTypes: val as TollVehicleType[] })
            }
            required
          />
        </SimpleGrid>

        <Switch
          label="Active"
          checked={v.isActive}
          onChange={(e) => setV({ ...v, isActive: e.currentTarget.checked })}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() => onSubmit(v)}
          >
            {editing ? "Save changes" : "Create"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
